// The WGSL twin of the instanced circle batch (`../../gfx/circles.ts`).
//
// The picture is the GLSL pair's, line for line. What the language forces:
// `select` takes its FALSE arm first, `atan(y, x)` is `atan2(y, x)`, `mod`
// is a reserved word and its GLSL meaning comes from `glMod`
// (`./common.ts`), and both stages live in one module over one varyings
// struct.
//
// `fwidth` stays, for the reason `./segments.ts` states: the call is at the
// top level of `fs`, and it is the antialiasing of every round mark of the
// map.

import type { AttribSpec } from '../../gfx/types';
import type { UniformSpec } from '../prelude';
import { cornerDecodeWgsl } from '../../gl/corner';
import { wgslModule } from './module';
import { GLMOD_WGSL } from './common';
import { BATCH_IDENTITY_WGSL, STROKE_ADD_WGSL } from '../addglow';

/** The varyings the two stages of the circle batch share. */
const CIRCLE_VARYINGS_WGSL = `struct VsOut {
  @builtin(position) pos: vec4f,
  @location(0) vP: vec2f,
  @location(1) vR: f32,
  @location(2) vHalfW: f32,
  @location(3) vFill: f32,
  @location(4) vArc: vec2f,
  @location(5) vDash: vec2f,
  @location(6) vColor: vec4f,
}
`;

/** A full turn, as the fragment stage reads it. */
const CIRCLE_TAU_WGSL = 'const TAU: f32 = 6.283185307179586;\n';

const CIRCLE_VERTEX_WGSL = `${BATCH_IDENTITY_WGSL}@vertex
fn vs(in: VsIn) -> VsOut {
  var out: VsOut;
${cornerDecodeWgsl()}
  let flags = i32(in.aFlags + 0.5);
  let k = select(1.0, 1.0 / U.uZoom, (flags & 1) != 0);   // F_SCREEN
  let px = 1.0 / max(U.uPxPerWorld, 1e-6);
  let filled = in.aStroke <= 0.0;
  let r = in.aRadius * k;
  let wWorld = select(in.aStroke * k, 0.0, filled);
  let wDraw = select(max(wWorld, px), 0.0, filled);
  // The quad is inflated by TWO device px and not by one. The fragment
  // ramps the edge over fwidth(d), and d moves one world px for one world
  // px of travel. A finite difference over one device px therefore moves d
  // by at most px on each axis, and fwidth adds the two axes, so it is at
  // most 2.0 * px. At an inflation of one px the distance at the four edge
  // midpoints of the quad is exactly px, which fwidth can reach, and the
  // ramp then leaves a faint tick there. MSAA made that tick visible. At
  // two px the distance on the quad edge is never less than the half-width
  // of the ramp, so the coverage is exactly zero on the edge with MSAA on
  // and with MSAA off.
  let ext = r + wDraw * 0.5 + 2.0 * px;
  let local = (corner - 0.5) * (2.0 * ext);
  let world = in.aCenter + local;
  out.pos = vec4f((U.uViewProj * vec3f(world, 1.0)).xy, 0.0, 1.0);
  out.vP = local;
  out.vR = r;
  out.vHalfW = wDraw * 0.5;
  out.vFill = select(0.0, 1.0, filled);
  out.vArc = in.aArc;
  out.vDash = in.aDash;
  let fade = select(min(1.0, wWorld * U.uPxPerWorld), 1.0, filled);
${STROKE_ADD_WGSL}
  return out;
}
`;

const CIRCLE_FRAGMENT_WGSL = `@fragment
fn fs(in: VsOut) -> @location(0) vec4f {
  let d = length(in.vP) - in.vR;
  let sd = select(abs(d) - in.vHalfW, d, in.vFill > 0.5);
  // The width comes from d and not from sd: abs() puts a crease on the
  // ring centreline, and fwidth spikes exactly there.
  let w = max(fwidth(d), 1e-6);
  var cov = 1.0 - smoothstep(-w, w, sd);
  if (in.vArc.y < TAU || in.vDash.x > 0.0) {
    let rel = glMod(atan2(in.vP.y, in.vP.x) - in.vArc.x, TAU);
    if (in.vArc.y < TAU) { cov *= step(rel, in.vArc.y); }
    if (in.vDash.x > 0.0) { cov *= step(fract(rel / TAU * in.vDash.x), in.vDash.y); }
  }
  return in.vColor * cov * U.uAlpha;
}
`;

/** The whole WGSL module of the circle batch: the header and both stages. */
export function circleModuleWgsl(
  specs: readonly UniformSpec[], attribs: readonly AttribSpec[],
): string {
  return wgslModule({
    uniforms: specs, attribs, varyings: CIRCLE_VARYINGS_WGSL,
    vertex: CIRCLE_TAU_WGSL + CIRCLE_VERTEX_WGSL,
    body: GLMOD_WGSL + CIRCLE_FRAGMENT_WGSL,
  });
}
